import React from "react";
import PropTypes from "prop-types";
import styles from "./HistoricalDataGraph.module.css";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

/* Tooltip */
const CustomTooltip = ({ active = false, payload = [], label = "" }) => {
  if (active && payload?.length) {
    return (
      <div className={styles.tooltipBox}>
        <div className={styles.tooltipDate}>{label}</div>

        <div className={styles.green}>
          NR to PDQ : {payload[0]?.value?.toLocaleString()} %
        </div>

        <div className={styles.blue}>
          NR to Alloc : {payload[1]?.value?.toLocaleString()} %
        </div>

        <div className={styles.orange}>
          Alloc To PDQ : {payload[2]?.value?.toLocaleString()} %
        </div>
      </div>
    );
  }

  return null;
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ),
};

/* number format */
const formatNumber = (value) => {
  if (value >= 10000000) return `${(value / 10000000).toFixed(1)}Cr`;
  if (value >= 100000) return `${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value;
};

/* date format */
const formatDate = (date) => {
  const d = new Date(date);

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "July",
    "Aug",
    "Sept",
    "Oct",
    "Nov",
    "Dec",
  ];

  return `${d.getDate()} ${months[d.getMonth()]}`;
};

const HistoricalDataGraph = ({ data = [], loading = false }) => {
  if (loading) {
    return (
      <div className={styles.shimmerWrapper}>
        <div className={styles.shimmerChart}></div>
      </div>
    );
  }

  if (!data?.length) {
    return <div className={styles.centerState}>No Data</div>;
  }

  const chartWidth = data.length > 10 ? `${data.length * 60}px` : "100%";

  return (
    <div className={styles.graphContainer}>
      <div className={styles.horizontalScroll}>
        <div className={styles.chartWrapper} style={{ width: chartWidth }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
              margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                horizontal={false}
                vertical
              />

              <XAxis
                dataKey="IngestedDate"
                tick={{ fontSize: 12 }}
                tickFormatter={formatDate}
                interval={0}
              />

              <YAxis tick={{ fontSize: 12 }} tickFormatter={formatNumber} />

              <Tooltip content={<CustomTooltip />} />

              <Legend wrapperStyle={{ fontSize: "12px" }} />

              <Line
                name="NR to PDQ"
                type="linear"
                dataKey="NR to PDQ"
                stroke="#2ca02c"
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />

              <Line
                name="NR to Alloc"
                type="linear"
                dataKey="NR to Alloc"
                stroke="#1f77b4"
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />

              <Line
                name="Alloc To PDQ"
                type="linear"
                dataKey="Alloc To PDQ"
                stroke="#ff7f0e"
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

HistoricalDataGraph.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      IngestedDate: PropTypes.string,
      "NR to PDQ": PropTypes.number,
      "NR to Alloc": PropTypes.number,
      "Alloc To PDQ": PropTypes.number,
    }),
  ),
  loading: PropTypes.bool,
};

export default HistoricalDataGraph;
